import { type Dispatch, type FunctionComponent, type SetStateAction, useState } from 'react'
import { Grid, GridItem, Link, TypographyV2 } from '@which/seatbelt'
import { CrossIcon } from '@which/seatbelt/src/components/Icons'

import classNames from 'classnames'

import type { Article, Product } from '../../../../../generated/frontend'
import { useCookieValue } from '../../../../hooks/useCookieValue'
import styles from './RecentlyViewedTextList.module.scss'

const DISMISS_BANNER_SESSION_COOKIE_NAME = 'dismiss_puwylo_banner'

type RecentlyViewedItem = Partial<Article> | Partial<Product>

type RecentlyViewedTextListProps = {
  items: RecentlyViewedItem[]
}

const getLink = (item: RecentlyViewedItem, index: number) => {
  let title: string | undefined
  let linkHref: string | undefined

  if (item.__typename === 'Article') {
    const { short_headline, headline, url } = item
    title = short_headline || headline
    linkHref = url
  } else if (item.__typename === 'Product') {
    const { manufacturer, model, productUrl } = item
    title = `${manufacturer?.name}, ${model}`
    linkHref = productUrl
  } else {
    console.warn('Unknown item type in RecentlyViewedTextList:', item)
    return null
  }

  if (!title || !linkHref) {
    console.warn('Missing title or linkHref in RecentlyViewedTextList item:', item)
    return null
  }

  return (
    <Link
      href={linkHref}
      className={styles.recentlyViewedTextListLink}
      data-index={(index + 1).toString()}
      data-which-id="recently viewed link"
      data-section="continue reading"
      data-card-name={title}
      data-spec="banner"
      data-personalisation="true"
    >
      {title}
    </Link>
  )
}

type CloseButtonProps = {
  setShowBanner: Dispatch<SetStateAction<boolean>>
}

const CloseButton: FunctionComponent<CloseButtonProps> = ({ setShowBanner }) => {
  const closeBanner = () => {
    document.cookie = `${DISMISS_BANNER_SESSION_COOKIE_NAME}=1; path=/; SameSite=Lax`
    setShowBanner(false)
  }

  return (
    <button
      onClick={closeBanner}
      data-testid="closeButton"
      data-which-id="recently viewed link close"
      data-section="continue reading"
      data-card-name="close"
      data-personalisation="true"
      data-spec="banner"
      data-action-group="close"
      className={styles.recentlyViewedTextListClose}
      aria-label="Close this recently viewed banner"
    >
      <CrossIcon width={15} height={15} data-which-id="pick-up-where-you-left-off-banner-close" />
    </button>
  )
}

export const RecentlyViewedTextList: FunctionComponent<RecentlyViewedTextListProps> = ({
  items,
}) => {
  const [showBanner, setShowBanner] = useState<boolean>(true)
  const dismissCookieValue = useCookieValue(DISMISS_BANNER_SESSION_COOKIE_NAME)

  if (!showBanner || dismissCookieValue === '1') {
    return null
  }

  return (
    <div className={styles.recentlyViewedTextListContainer}>
      <Grid
        className={classNames('w-page-wrapper', styles.recentlyViewedTextList)}
        data-testid="pick-up-where-you-left-off-banner"
      >
        <GridItem span={{ medium: 12, large: 12 }} className={styles.recentlyViewedTextListBox}>
          <TypographyV2
            tag="span"
            textStyle="sb-text-body-default-regular"
            className={styles.recentlyViewedTextListTitle}
          >
            Continue reading:
          </TypographyV2>

          <ul className={styles.recentlyViewedTextListItems}>
            {items.map((item, index) => (
              <li key={index} className={styles.recentlyViewedTextListItem}>
                {getLink(item, index)}
              </li>
            ))}
          </ul>
          <div className={styles.recentlyViewedTextListCloseWrapper}>
            <CloseButton setShowBanner={setShowBanner} />
          </div>
        </GridItem>
      </Grid>
    </div>
  )
}
